// ============================================================
// Staff Discipline Page — Staff Management
// ============================================================

import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Paper,
  Chip,
  MenuItem,
  Avatar,
  Card,
  CardContent,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  History,
  ShieldAlert,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  ShieldOff,
  Users,
  Edit,
  Filter,
} from 'lucide-react';
import {
  useStaffList,
  useDepartments,
  useStaffDisciplineStatusEnums,
  useUpdateStaffDiscipline,
} from '@/hooks/useStaffManagement';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/constants/permissions';
import toast from 'react-hot-toast';
import { UpdateDisciplineModal } from './components/UpdateDisciplineModal';
import { DisciplineHistoryModal } from './components/DisciplineHistoryModal';

const STATUS_CHIP_MAP = {
  SATISFACTORY: {
    label: 'Satisfactory',
    color: 'success',
    icon: CheckCircle2,
    bgcolor: '#DCFCE7',
    textColor: '#15803D',
    borderColor: '#22C55E',
  },
  WARNING: {
    label: 'Warning',
    color: 'warning',
    icon: AlertTriangle,
    bgcolor: '#FEF9C3',
    textColor: '#854D0E',
    borderColor: '#EAB308',
  },
  MAJOR_WARNING: {
    label: 'Major Warning',
    color: 'warning',
    icon: AlertCircle,
    bgcolor: '#FFEDD5',
    textColor: '#9A3412',
    borderColor: '#F97316',
  },
  CRITICAL: {
    label: 'Critical',
    color: 'error',
    icon: ShieldOff,
    bgcolor: '#FEE2E2',
    textColor: '#991B1B',
    borderColor: '#EF4444',
  },
};

const getStaffDisciplineStatus = (staff) => {
  if (!staff) return null;
  if (typeof staff.discipline === 'string') return staff.discipline;
  if (staff.discipline && typeof staff.discipline === 'object') {
    return staff.discipline.status || staff.discipline.discipline_status || staff.discipline.name;
  }
  if (staff.discipline_status) return staff.discipline_status;
  if (staff.current_discipline_status) return staff.current_discipline_status;
  if (staff.employment_status) return staff.employment_status;
  return null;
};

export function StaffDisciplinePage() {
  usePageMeta('Staff Discipline | Staff Management', [
    { label: 'Staff Management', path: '/staff-management' },
    { label: 'Staff Discipline' },
  ]);

  const { can } = usePermission();
  const canManage = can(PERMISSIONS.STAFF_MANAGE_UPDATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);

  const { data: staffList = [], isLoading, error, refetch } = useStaffList();
  const { data: departments = [] } = useDepartments();
  const { data: statusEnums = [] } = useStaffDisciplineStatusEnums();
  const updateMutation = useUpdateStaffDiscipline();

  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Modals state
  const [updateModalStaff, setUpdateModalStaff] = useState(null);
  const [historyModalStaff, setHistoryModalStaff] = useState(null);

  // Dynamic available statuses from backend staffList + statusEnums
  const availableStatuses = Array.from(
    new Set([
      ...statusEnums.map((e) => (typeof e === 'string' ? e : e?.value || e?.status || e?.name)),
      ...staffList.map((s) => getStaffDisciplineStatus(s)).filter(Boolean),
    ])
  ).filter(Boolean);

  // Compute Metrics dynamically
  const totalStaff = (staffList || []).length;
  const satisfactoryCount = (staffList || []).filter((s) => {
    const st = String(getStaffDisciplineStatus(s) || '').toUpperCase();
    return st === 'SATISFACTORY' || st === 'ACTIVE';
  }).length;
  const warningCount = (staffList || []).filter((s) => {
    const st = String(getStaffDisciplineStatus(s) || '').toUpperCase();
    return st === 'WARNING' || st === 'MAJOR_WARNING';
  }).length;
  const criticalCount = (staffList || []).filter((s) => {
    const st = String(getStaffDisciplineStatus(s) || '').toUpperCase();
    return st === 'CRITICAL' || st === 'SUSPENDED';
  }).length;

  // Filter staff list
  const filteredStaff = (staffList || []).filter((staff) => {
    // Search query match
    if (search.trim()) {
      const q = search.toLowerCase();
      const name = `${staff.first_name || ''} ${staff.last_name || ''}`.toLowerCase();
      const code = (staff.employee_code || staff.staff_code || '').toLowerCase();
      const email = (staff.email || '').toLowerCase();
      if (!name.includes(q) && !code.includes(q) && !email.includes(q)) {
        return false;
      }
    }

    // Department match
    if (departmentFilter !== 'ALL') {
      const deptId = staff.department_id || staff.department?.id;
      if (deptId !== departmentFilter) return false;
    }

    // Status match
    if (statusFilter !== 'ALL') {
      const currentStatus = getStaffDisciplineStatus(staff);
      if (currentStatus !== statusFilter) return false;
    }

    return true;
  });

  const handleOpenUpdate = (staff) => {
    setUpdateModalStaff(staff);
  };

  const handleOpenHistory = (staff) => {
    setHistoryModalStaff(staff);
  };

  const handleSaveDiscipline = async ({ staffId, status, remarks }) => {
    try {
      await updateMutation.mutateAsync({ staffId, status, remarks });
      toast.success('Staff discipline record updated successfully');
      setUpdateModalStaff(null);
      refetch();
    } catch (err) {
      toast.error(err.message || 'Failed to update staff discipline');
    }
  };

  const columns = [
    {
      field: 'staff',
      headerName: 'Staff Member',
      flex: 1,
      minWidth: 260,
      renderCell: (params) => {
        const row = params.row;
        const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.name || 'Unnamed';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
            <Avatar
              src={row.photo_url || row.avatar_url || row.profile_picture}
              sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 600 }}
            >
              {fullName.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                {fullName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                {row.email || 'No email provided'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'employee_code',
      headerName: 'Employee Code',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {params.row.employee_code || params.row.staff_code || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'department',
      headerName: 'Department',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {params.row.department?.name || 'Unassigned'}
        </Typography>
      ),
    },
    {
      field: 'designation',
      headerName: 'Designation',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.row.designation?.name || 'Unassigned'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Discipline Status',
      width: 180,
      renderCell: (params) => {
        const rawStatus = getStaffDisciplineStatus(params.row);
        if (!rawStatus) {
          return (
            <Chip
              label="No Record"
              size="small"
              variant="outlined"
              sx={{ color: 'text.secondary', fontWeight: 600, px: 0.5 }}
            />
          );
        }

        const upperStatus = String(rawStatus).toUpperCase();
        const cfg = STATUS_CHIP_MAP[upperStatus] || {
          label: String(rawStatus).replace(/_/g, ' ').toUpperCase(),
          color: 'default',
          icon: CheckCircle2,
          bgcolor: '#DCFCE7',
          textColor: '#15803D',
          borderColor: '#22C55E',
        };
        const IconComp = cfg.icon || CheckCircle2;

        return (
          <Chip
            icon={<IconComp size={14} />}
            label={cfg.label || rawStatus}
            size="small"
            sx={{
              bgcolor: cfg.bgcolor,
              color: cfg.textColor,
              fontWeight: 700,
              px: 0.5,
              border: `1px solid ${cfg.borderColor || 'transparent'}`,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 170,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', gap: 1 }}>
          <Tooltip title="View Discipline History">
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<History size={14} />}
              onClick={() => handleOpenHistory(params.row)}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, px: 1, py: 0.3 }}
            >
              History
            </Button>
          </Tooltip>

          {canManage && (
            <Tooltip title="Update Status">
              <Button
                size="small"
                variant="contained"
                startIcon={<Edit size={14} />}
                onClick={() => handleOpenUpdate(params.row)}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, px: 1, py: 0.3 }}
              >
                Update
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (error) {
    return (
      <Box>
        <ErrorState error={error} onRetry={refetch} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Staff Discipline"
        subtitle="Track staff conduct, issue warnings, and manage official discipline audit history"
      />

      {/* KPI Metric Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 3,
          width: '100%',
        }}
      >
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Total Staff
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {totalStaff}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Users size={22} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Satisfactory
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5 }}>
                  {satisfactoryCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  bgcolor: '#DCFCE7',
                  color: '#15803D',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircle2 size={22} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Warnings
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#9A3412', mt: 0.5 }}>
                  {warningCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  bgcolor: '#FFEDD5',
                  color: '#9A3412',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AlertTriangle size={22} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Critical Status
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main', mt: 0.5 }}>
                  {criticalCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  bgcolor: '#FEE2E2',
                  color: '#991B1B',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ShieldOff size={22} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filter and Search Controls */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <TextField
            placeholder="Search staff by name, code or email..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ flex: 1, minWidth: { xs: '100%', md: 240 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
            {/* Department Filter */}
            <TextField
              select
              size="small"
              label="Department"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              sx={{ minWidth: 170, flex: { xs: 1, sm: 'none' } }}
            >
              <MenuItem value="ALL">All Departments</MenuItem>
              {(departments || []).map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Status Filter */}
            <TextField
              select
              size="small"
              label="Discipline Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 170, flex: { xs: 1, sm: 'none' } }}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              {availableStatuses.map((st) => (
                <MenuItem key={st} value={st}>
                  {STATUS_CHIP_MAP[st]?.label || String(st).replace(/_/g, ' ').toUpperCase()}
                </MenuItem>
              ))}
            </TextField>

            {(search || departmentFilter !== 'ALL' || statusFilter !== 'ALL') && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setSearch('');
                  setDepartmentFilter('ALL');
                  setStatusFilter('ALL');
                }}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Data Grid */}
      <Paper variant="outlined" sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <DataGrid
          autoHeight
          rows={filteredStaff}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id || Math.random().toString()}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </Paper>

      {/* Modals */}
      <UpdateDisciplineModal
        open={!!updateModalStaff}
        onClose={() => setUpdateModalStaff(null)}
        staff={updateModalStaff}
        statusEnums={statusEnums}
        onSubmit={handleSaveDiscipline}
        loading={updateMutation.isPending}
      />

      <DisciplineHistoryModal
        open={!!historyModalStaff}
        onClose={() => setHistoryModalStaff(null)}
        staff={historyModalStaff}
      />
    </Box>
  );
}
